import React from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Button,
    IconButton,
    Avatar,
} from '@mui/material';
import { FiArrowLeft, FiClipboard } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import './MyTests.css';
import { motion } from 'framer-motion';
import { Chart } from 'primereact/chart';
import 'primereact/resources/themes/lara-light-blue/theme.css'; // optional theme
import 'primereact/resources/primereact.min.css';               // PrimeReact styles
import 'primeicons/primeicons.css';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);


const MyTests = () => {
    const navigate = useNavigate();

    const testData = [
        {
            id: 1,
            exam: 11,
            topic: 'Math Algebric Expression1',
            subject: 'Math',
            date: '2025-07-10',
            score: '85%',
            status: 'Completed',
        },
        {
            id: 2,
            exam: 12,
            topic: 'Physics Thermo',
            subject: 'Physics',
            date: '2025-07-08',
            score: '78%',
            status: 'Completed',
        },
        {
            id: 3,
            exam: 13,
            topic: 'Chemistry Bonding',
            subject: 'Chemistry',
            date: '2025-07-05',
            score: '92%',
            status: 'Completed',
        },
        {
            id: 4,
            exam: 14,
            topic: 'Biology Reproduction',
            subject: 'Biology',
            date: '2025-07-04',
            score: '88%',
            status: 'Completed',
        },
        {
            id: 5,
            exam: 15,
            topic: 'English Grammar',
            subject: 'English',
            date: '2025-07-02',
            score: '76%',
            status: 'Completed',
        },
        {
            id: 6,
            exam: 16,
            topic: 'Math Linear Equations',
            subject: 'Math',
            date: '2025-06-30',
            score: '81%',
            status: 'Completed',
        },
        {
            id: 7,
            exam: 17,
            topic: 'Physics Kinematics',
            subject: 'Physics',
            date: '2025-06-28',
            score: '69%',
            status: 'Completed',
        },
        {
            id: 8,
            exam: 18,
            topic: 'History Ancient India',
            subject: 'History',
            date: '2025-06-25',
            score: '74%',
            status: 'Completed',
        },
        {
            id: 9,
            exam: 19,
            topic: 'Geography Climate',
            subject: 'Geography',
            date: '2025-06-23',
            score: '90%',
            status: 'Completed',
        },
    ];

    const chartData = {
        labels: testData.map(test => test.topic),
        datasets: [
            {
                data: testData.map(test => parseFloat(test.score)), // Converts '85%' -> 85
                backgroundColor: ['#007BFF', '#00BCD4', '#4CAF50', '#FF9800', '#E91E63'],
                hoverBackgroundColor: ['#0056b3', '#0097a7', '#388e3c', '#f57c00', '#c2185b']
            }
        ]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    color: '#333',
                    font: {
                        size: 14,
                        weight: 'bold'
                    }
                }
            },
            tooltip: {
                callbacks: {
                    label: function (tooltipItem) {
                        const value = chartData.datasets[0].data[tooltipItem.dataIndex];
                        return `${chartData.labels[tooltipItem.dataIndex]}: ${value}%`;
                    }
                }
            }
        },
        animation: {
            animateRotate: true,
            animateScale: true
        },
        datalabels: {
            color: '#000',
            font: {
                weight: 'bold',
                size: 14
            },
            formatter: (value) => `${value}%`
        }
    };

    const totalScore = (
        testData.reduce((sum, test) => sum + parseFloat(test.score), 0) / testData.length
    ).toFixed(2);

    return (
        <Box p={2}>
            {/* Header */}
            <Box display="flex" alignItems="center" mb={5} mt={2}>
                <IconButton
                    onClick={() => navigate(-1)}
                    sx={{
                        background: 'linear-gradient(135deg, #00bcd4, #007bff)',
                        color: '#fff',
                        '&:hover': { transform: 'scale(1.1)' },
                        borderRadius: '12px',
                        padding: '8px 12px',
                        mr: 2
                    }}
                >
                    <FiArrowLeft />
                </IconButton>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: 'bold',
                        background: 'linear-gradient(90deg, #007BFF, #00d4ff)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        textTransform: 'uppercase',
                        textAlign: 'center',
                        mx: 'auto'
                    }}
                >
                    My Tests
                </Typography>
            </Box>

            {/* Test Cards */}
            <Grid container spacing={2} justifyContent="center">
                {testData.map((test) => (
                    <Grid item xs={12} sm={8} md={6} lg={5} key={test.id}>
                        <motion.div
                            whileTap={{ scale: 0.97 }}
                            whileHover={{ scale: 1.02, boxShadow: '0px 4px 12px rgba(0,0,0,0.1)' }}
                            transition={{ type: 'spring', stiffness: 200, damping: 12 }}
                            className="test-card"
                        >
                            <Card elevation={0}>
                                <CardContent>
                                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                                        <motion.div
                                            whileHover={{ rotate: -10, scale: 1.1 }}
                                            transition={{ type: 'spring', stiffness: 300 }}
                                        >
                                            <Avatar className="test-icon">
                                                <FiClipboard />
                                            </Avatar>
                                        </motion.div>
                                        <Box>
                                            <Typography variant="h6" fontWeight="bold">
                                                {test.topic}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {test.subject} • {test.date}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
                                        <Typography>
                                            <span
                                                style={{
                                                    background: 'linear-gradient(90deg, #00c853, #64dd17)',
                                                    WebkitBackgroundClip: 'text',
                                                    WebkitTextFillColor: 'transparent',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                Status:
                                            </span>{' '}
                                            <strong>{test.status}</strong>
                                        </Typography>

                                        <Typography>
                                            <span
                                                style={{
                                                    background: 'linear-gradient(90deg, #c62828, #e53935)',
                                                    WebkitBackgroundClip: 'text',
                                                    WebkitTextFillColor: 'transparent',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                Score:
                                            </span>{' '}
                                            <strong>{test.score}</strong>
                                        </Typography>


                                    </Box>

                                    {/* Action Buttons */}
                                    <Box display="flex" gap={2} mt={2} flexWrap="wrap">
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            onClick={() => navigate('/view-answers', { state: test })}
                                        >
                                            📄 View Answers
                                        </Button>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            onClick={() => navigate('/test-report', { state: test })}
                                        >
                                            📊 View Report
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </motion.div>
                    </Grid>
                ))}
            </Grid>

            {/* add code for pie chart */}
            <Box mt={4}>
                <Typography
                    variant="h5"
                    sx={{
                        mb: 2, fontWeight: 'bold', 
                        textAlign: 'center', 
                        background: 'linear-gradient(90deg, #007BFF, #00d4ff)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        textTransform: 'uppercase',
                        mt: 10,
                        mb: 5
                    }}
                >
                    Exam-wise Performance
                </Typography>

                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 600,
                        mx: 'auto',
                        position: 'relative',
                        height: 400
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0
                        }}
                    >
                        <div style={{ height: '100%', width: '100%' }}>
                            <Chart type="pie" data={chartData} options={chartOptions} plugins={[ChartDataLabels]} />
                        </div>

                    </Box>
                </Box>


                <Typography
                    align="center"
                    fontWeight="bold"
                    fontSize="1.2rem"
                    mt={1}
                    mb={4}
                >
                    Total Score: {totalScore}%
                </Typography>
            </Box>

        </Box>
    );
};

export default MyTests;
